import { r as createLogger } from "./logger-BU8YqMGU.js";
import { validateQuad } from "./ar/qr/qr-pose.js";
//#region ../src/ar/qr/detection-scheduler.ts
const log = createLogger("DetectionScheduler");
const defaultNow = () => typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
function createDetectionScheduler(config) {
	const { detect, minIntervalMs, requiredLockCount = 3, now = defaultNow, onLocked, onMiss, onError } = config;
	let inFlight = false;
	let consecutiveLocks = 0;
	let lastStart = -Infinity;
	return {
		get inFlight() {
			return inFlight;
		},
		get consecutiveLocks() {
			return consecutiveLocks;
		},
		get locked() {
			return consecutiveLocks >= requiredLockCount;
		},
		offerFrame(image) {
			if (inFlight) return;
			const t = now();
			if (t - lastStart < minIntervalMs) return;
			lastStart = t;
			inFlight = true;
			(async () => detect(image))().then((result) => {
				if (result) {
					consecutiveLocks = Math.min(consecutiveLocks + 1, requiredLockCount);
					if (consecutiveLocks >= requiredLockCount) try {
						onLocked?.(result);
					} catch (err) {
						log.error("onLocked callback threw:", err);
					}
				} else {
					consecutiveLocks = 0;
					try {
						onMiss?.();
					} catch (err) {
						log.error("onMiss callback threw:", err);
					}
				}
			}).catch((err) => {
				consecutiveLocks = 0;
				try {
					onError?.(err);
				} catch (callbackErr) {
					log.error("onError callback threw:", callbackErr);
				}
			}).finally(() => {
				inFlight = false;
			});
		}
	};
}
/** The QR path: a detection scheduler whose result is a {@link QrPoseSolution}. */
function createQrDetectionScheduler(config) {
	return createDetectionScheduler(config);
}
//#endregion
//#region ../src/ar/qr/qr-detection-controller.ts
/**
* Create the thin RAW producer. On each accepted decode it emits a
* {@link RawQrObservation} via `recordDetection`; it does not size or solve.
*/
function createQrDetectionController(deps) {
	const { detect, getCameraPose, getProjectionMatrix, recordDetection, now, minIntervalMs = 0, requiredLockCount = 2, onStatus } = deps;
	const timestampNow = now ?? (() => Date.now());
	let status = "idle";
	function setStatus(next) {
		if (status === next) return;
		status = next;
		onStatus?.(next);
	}
	async function runDetect(image) {
		if (status === "idle") setStatus("scanning");
		const detection = await detect(image);
		if (!detection) return null;
		if (!validateQuad(detection.corners).ok) return null;
		const cameraPose = getCameraPose();
		const projectionMatrix = getProjectionMatrix();
		if (!cameraPose || !projectionMatrix) return null;
		return {
			text: detection.text,
			corners: detection.corners,
			cameraPose,
			projectionMatrix,
			imageWidth: image.width,
			imageHeight: image.height,
			timestamp: timestampNow()
		};
	}
	const scheduler = createDetectionScheduler({
		detect: runDetect,
		minIntervalMs,
		requiredLockCount,
		...now ? { now } : {},
		onLocked: (observation) => {
			recordDetection(observation);
			setStatus("tracking");
		},
		onMiss: () => {
			if (status === "tracking") setStatus("scanning");
		}
	});
	return {
		offerFrame(image) {
			scheduler.offerFrame(image);
		},
		get status() {
			return status;
		},
		reset() {
			setStatus("idle");
		}
	};
}
//#endregion
export { createDetectionScheduler as n, createQrDetectionScheduler as r, createQrDetectionController as t };
